import { ref } from 'vue'
import CryptoJS from 'crypto-js';
import DOMPurify from 'dompurify'
import moment from 'moment';

/**
 * Helper to format Date object or ISO string into YYYY-MM-DD
 */
export const formatDateToISO = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    const year  = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day   = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}
  
/**
 * Initialize form default state based on configuration schema
 *  
 */
export const initializeFormData = (formConfig, formData, defaultValues = {}) => {
    const sections = formConfig.tab || formConfig.card || []
    
    sections.forEach(section => {
        section.input.forEach(field => {

            if (defaultValues && defaultValues[field.id] !== undefined) {
                formData[field.id] = defaultValues[field.id]
            } 

            else if (field.type === 'checkbox') {
                formData[field.id] = false
            } else if (field.type === 'date') {
                if (field.placeholder?.toLowerCase() === "today's date") {
                    formData[field.id] = formatDateToISO(new Date())
                } else {
                    formData[field.id] = field.placeholder && !isNaN(Date.parse(field.placeholder))
                    ? formatDateToISO(field.placeholder)
                    : ''
                }
            } else {
                formData[field.id] = ''
            }
        })
    })
}
  
/**
 * Setup for readonly form and input
 */
export const isFieldDisabled = (formConfig, field, isLoading) => {
    if (isLoading) return true
    
    if (field.readonly !== undefined) {
      return Boolean(field.readonly)
    }
    
    // Fallback to top-level formConfig.readonly
    return Boolean(formConfig.readonly)
}

/**
 * Trigger native browser date picker
 */
export const openDatePicker = (fieldId, datePickerRefs, isLoading) => {
    if (isLoading) return

    const picker = datePickerRefs[fieldId]
    if (picker && typeof picker.showPicker === 'function') {
        try {
            picker.showPicker()
        } catch (err) {
            console.debug('showPicker failed or unsupported:', err)
        }
    }
}
  
/**
 * Handle date picker selection
 */
export const handleDateChange = (event, fieldId, formData) => {
    const selectedValue = event.target.value
    if (selectedValue) {
        formData[fieldId] = formatDateToISO(selectedValue)
    }
}
  
/**
 * Computes top level container width class
 */
export const getContainerWidthClass = (width) => {
    // GEMINI Here
    // if (!width) return 'max-w-4xl'
    // if (width === 100 || width === '100%' || width === 'full') return 'w-full'
    // return `max-w-[${width}%]`

        // It's mine !!!! 
        if (!width) return 'max-w-4xl'
        if (width === 100 || width === '100%' || width === 'full') return 'w-full'
    
        // Map common width percentage values so Tailwind picks them up during build
        const widthMap = {
             25: 'max-w-[25%]'
            ,30: 'max-w-[30%]'
            ,40: 'max-w-[40%]'
            ,50: 'max-w-[50%]'
            ,60: 'max-w-[60%]'
            ,70: 'max-w-[70%]'
            ,75: 'max-w-[75%]'
            ,80: 'max-w-[80%]'
            ,90: 'max-w-[90%]'
        }
    
        // Fallback to inline style if an exact key isn't found
        return widthMap[width] || ''    
}
  
/**
 * Returns dynamic Tailwind CSS grid columns for each card based on 'maxcol'
 */
export const getGridClass = (card) => {
    // GEMINI
    // if (card.layout !== 'row') return 'flex flex-col gap-5'
    // const maxCol  = card.maxcol || 3
    // const gridMap = {
    //    1: 'grid grid-cols-1 gap-6'
    //   ,2: 'grid grid-cols-1 sm:grid-cols-2 gap-6'
    //   ,3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
    //   ,4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'
    //   ,5: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6'
    //   ,6: 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-6'
    //   ,7: 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-6'
    //   ,8: 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-8 gap-6'
    // }
  
    // return gridMap[maxCol] || `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${maxCol} gap-6`

    // It's mine here, modif
    if (card.layout !== 'row') return 'flex flex-col gap-5'
    const maxCol = Number(card.maxcol) || 3
    // Explicit grid class lookup up to 12 columns
    const gridMap = {
          1 : 'grid grid-cols-1 gap-6'
        , 2 : 'grid grid-cols-1 sm:grid-cols-2 gap-6'
        , 3 : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
        , 4 : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'
        , 5 : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6'
        , 6 : 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-6'
        , 7 : 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-6'
        , 8 : 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-8 gap-6'
        , 9 : 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-9 gap-6'
        , 10: 'grid grid-cols-1 sm:grid-cols-5 lg:grid-cols-10 gap-6'
        , 11: 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-11 gap-6'
        , 12: 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-12 gap-6'
    }
  
    return gridMap[maxCol] || gridMap[12]


}

/**
 * Read data from some end point with parameter 
 */
export  const ReadSaveEndPoint =  async (formConfig, formData) => {
    const isLoading      = ref(false)
    const errorMessage   = ref('')
    const successMessage = ref('')
    const resp_api       = ref('')


    const payload = new URLSearchParams()
    Object.keys(formData).forEach((key) => {
        payload.append(key, formData[key])
    })

    // Get data for input, if there state is Edit data !!! 
    try {
        const baseUrl    = import.meta.env.VITE_API_URL || ''
        const endpoint   = formConfig.save_endpoint
        const httpMethod = (formConfig.method || 'post').toUpperCase()
        const token      = localStorage.getItem('auth_token')

        const response = await fetch(`${baseUrl}${endpoint}`, {
                                    method : httpMethod,
                                    headers: { 
                                        'Content-Type' : 'application/x-www-form-urlencoded',
                                        'Authorization': `Bearer ${token}`
                                    },
                                    body: payload
                                })

        const api_resp = await response.json().catch(() => null)
        resp_api.value = JSON.stringify(api_resp);
        if (response.ok && api_resp?.success) {
            successMessage.value = api_resp.message || 'Data submitted successfully'
        } else {
            errorMessage.value = api_resp?.message || `Error ${response.status}: Request failed`                    
        }
    } catch (error) {
        console.error('API Error:', error)
        errorMessage.value = error.message || 'Network error or unable to reach the server.'
        // Show Failed here !!!
    } finally {
        isLoading.value = false
    }
    console.log("📢[formUtils.js:208]: ", {resp_api, successMessage, errorMessage });
    return {resp_api, successMessage, errorMessage }
}

/**
 * Recursively gets inputs from a section whether they are flat or nested inside `layout`
 */
export const getSectionInputs = (item) => {
    // if (item?.input) return item.input
    // if (Array.isArray(item?.layout)) {
    //     return item.layout.flatMap(sub => sub.input || [])
    // }
    // return []
    if (!item) return []
    if (Array.isArray(item.input)) {
        return item.input
    }
    if (Array.isArray(item.layout)) {
        return item.layout.flatMap(sub => getSectionInputs(sub))
    }
    return []
}

/**
 * Parses pipe-separated string IDs into key, label, and placeholder
 * Example: "column||Column Display||What Column..." -> { key: "column", label: "Column Display", placeholder: "What Column..." }
 */
export const parseFieldConfig = (idString) => {
    if (!idString) return { key: '', label: '', placeholder: '' }
    const parts = idString.split('||')
    return {
        key        : parts[0] || '',
        label      : parts[1] || parts[0] || '',
        placeholder: parts[2] || parts[1] || parts[0] || ''
    }
}

/**
 * Initializes form data for the new layout structure (cards and tabs)
 */
export const before_new_initializeFormData = (config) => {
    const data = {}
    if (!config?.layout) return data

    const processInputs = (inputs) => {
        inputs.forEach((field) => {
            const { key } = parseFieldConfig(field.id)
            if (key && !field.button) data[key] = ''
        })
    }

    config.layout.forEach((section) => {
        if (section.style === 'tab' && section.layout) {
            section.layout.forEach((tab) => processInputs(tab.input || []))
        } else {
            processInputs(getSectionInputs(section))
        }
    })

    return data
}

export const new_initializeFormData = (config) => {
    const data = {}
    if (!config?.layout) return data

    const processInputs = (inputs) => {
        inputs.forEach((field) => {
            const { key } = parseFieldConfig(field.id)
            if (key && !field.button) {
                if (field.type === 'checkbox') {
                    // Jika ada opsi banyak, default-kan ke Array []
                    data[key] = (field.options || field.opt) ? [] : false
                } else if (field.type === 'switch') {
                    data[key] = false
                } else {
                    data[key] = ''
                }
            }
        })
    }

    config.layout.forEach((section) => {
        if (section.style === 'tab' && section.layout) {
            section.layout.forEach((tab) => processInputs(tab.input || []))
        } else {
            processInputs(getSectionInputs(section))
        }
    })

    return data
}


/**
 * Scans formConfig for fields with dynamic 'opt_url' attributes and fetches options from API
 */
export const loadDynamicFieldOptions = async (formConfig) => {
    if (!formConfig?.layout) return

    const processField = async (field) => {
        // check if they have opt_url ?  if yes then go get from api
        if (field.opt_url) {
            try {
                // Uses HitBackEnd directly inside formUtils.js
                const res = await HitBackEnd(field.opt_url, 'GET', {})
                if (res && res.ok) {
                    const data = await res.json()
                    // Convert Resp to array value !!
                    const fetchedOptions = Array.isArray(data) ? data : (data.data || data.options || [])
                    
                    if (field.options !== undefined) {
                        field.options = fetchedOptions
                    } else {
                        field.opt = fetchedOptions
                    }
                }
            } catch (err) {
                console.error(`Failed to load options for field [${field.id}]:`, err)
            }
        } else if (field.post_url) {
            try {
                // Uses HitBackEnd directly inside formUtils.js
                const res = await HitBackEnd(field.post_url, 'POST', field.payload)
                if (res && res.ok) {
                    const data = await res.json()
                    // console.log("🚀 ~ processField ~ data:", data)
                    // Convert Resp to array value !!
                    const fetchedOptions = Array.isArray(data) ? data : (data.data || data.options || [])
                    // console.log("🚀 ~ processField ~ fetchedOptionsPOST:", fetchedOptions)
                    
                    if (field.options !== undefined) {
                        field.options = fetchedOptions
                    } else {
                        field.opt = fetchedOptions
                    }
                }
            } catch (err) {
                console.error(`Failed to load options for field [${field.id}]:`, err)
            }
        }
    }

    for (const section of formConfig.layout) {
        if (section.style === 'tab' && section.layout) {
            for (const tab of section.layout) {
                const inputs = tab.input || []
                await Promise.all(inputs.map(field => processField(field)))
            }
        } else {
            const inputs = getSectionInputs(section)
            await Promise.all(inputs.map(field => processField(field)))
        }
    }
}

/**
 * Dynamic inline grid style to guarantee proper columns for any maxcol value (e.g. 7, 3, 12)
 */
export const getGridStyle = (item) => {
    const maxCol = Number(item?.maxcol) || 1
    return {
        display            : 'grid',
        gridTemplateColumns: `repeat(${maxCol}, minmax(0, 1fr))`,
        gap                : '0.75rem'
    }
}

/**
 * Resolves section width dynamically (e.g. width: 50 -> "50%", width: 100 -> "100%")
 */
export const getSectionWidthStyle = (section) => {
    const width = Number(section?.width) || 100
    return {
        width: `${width}%`,
        flex: `0 0 ${width}%`
    }
}


/**
 * General Fetch to something from the backend. 
*/
export  const HitBackEnd =  async ( UrlPoint, method_, formData) => {
    const payload = new URLSearchParams()
    Object.keys(formData).forEach((key) => {
        const val = formData[key]
        
        // If value of val is an Objecct or Array 
        if (val !== null && typeof val === 'object') {
            payload.append(key, JSON.stringify(val)) // Change to JSON String 
        } else {
            payload.append(key, val ?? "")
        }
    })

    // Get data for input, if there state is Edit data !!! 
    let response;
    try {
        const SECRET_KEY       = import.meta.env.VITE_APP_SECRET
        const SECRET_URL       = import.meta.env.VITE_URL_SECRET

        const endpoint         = UrlPoint
        const httpMethod       = CryptoJS.AES.encrypt(method_.toUpperCase(), SECRET_KEY).toString();
        const token            = localStorage.getItem('auth_token')
        
        const encryptedUrl     = CryptoJS.AES.encrypt(endpoint, SECRET_KEY).toString();
        const encryptedTarget  = CryptoJS.AES.encrypt(endpoint, SECRET_URL).toString();
        const encryptedPayload = CryptoJS.AES.encrypt(payload.toString(), SECRET_KEY).toString();
        const targeturl        = '/api/v.1.0/' + btoa(encodeURIComponent(encryptedTarget))
        const baseUrl          =  import.meta.env.VITE_PROXY_URL
        
        // console.log("🚀 ~ HitBackEnd ~ endpoint:", endpoint)
        // console.log("🚀 ~ file: formUtils.js:310 ~ targeturl:", targeturl)
        // console.log("🚀 ~ file: formUtils.js:308 ~ payload.toString():", payload.toString())
        // console.log("📢[formUtils.js:390]: method: ", method_);
        if(method_== "GET") {
            response = await fetch(`${baseUrl}/secure-proxy`, {
                method : 'GET'
                ,headers: { 
                    'Content-Type' : 'application/json'
                    , 'Authorization': `Bearer ${token}`
                }
            });
        } else {
            response = await fetch(`${baseUrl}/secure-proxy`, {
                method : 'POST'
                ,headers: { 
                    'Content-Type' : 'application/json'
                    , 'Authorization': `Bearer ${token}`
                }
                ,body: JSON.stringify({
                    t: encryptedUrl
                    , m: httpMethod
                    , p: encryptedPayload
                })
            });
        }
        
        if(!response.ok) {
            console.log("🚀 ~ file: formUtils.js:326 ~ response:", response)
        }
    } catch (error) {
        console.error('API Error:', error)
        // errorMessage.value = error.message || 'Network error or unable to reach the server.'

    } 
    return response
}


/**
 * Re-Arrange data from server. 
*/
export  const ReArrange =  ( requestOrder, serverData) => {
    const newFormat = serverData.map(item => {
        const formattedItem = {};
        
        requestOrder.forEach(key => {
            if (key in item) {
                formattedItem[key] = item[key];
            }
        });
        
        return formattedItem;
    });

    // console.log("🚀 ~ ReArrange ~ newFormat:", newFormat);
    return newFormat;
}


/**
 * General Fetch Function and get what form will process and return the payload
*/
export  const findJsonSection =  async ( formData_ , section_ ) => {
    const sectionInputs = getSectionInputs(section_)
    const allowedKeys = sectionInputs.map(f => parseFieldConfig(f.id).key)

    // Build payload with ONLY current card's keys
    const payload = {}
    allowedKeys.forEach(key => {
        if (key && formData_[key] !== undefined) {
            const cleanVal = sanitizeInput(formData_[key])
            payload[key] = cleanVal
            // payload[key] = formData_[key]
        }
    })
    return payload
}

function sanitizeInput(val) {
    if (typeof val === 'string') return DOMPurify.sanitize(val.trim())
    return val
}

/**
 * Convert from html to clean json
*/
export const getCleanJson = (htmlString) => {
    try {
        // Remove HTML tags (seperti <p> atau <br>)
        const cleanText = htmlString.replace(/<[^>]*>/g, '').trim();
        return JSON.parse(cleanText);
    } catch (e) {
        console.error("Format data bukan JSON valid:", e);
        return null;
    }
};

/**
 * Just for counting duration process
*/
export const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const hours        = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
    const minutes      = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
    const seconds      = String(totalSeconds % 60).padStart(2, '0')
    const milli        = String(Math.floor(ms % 1000)).padStart(3, '0')
    const TimeResp     = {
            hmss: `${hours}:${minutes}:${seconds} '${milli}`
          , mss : `${minutes}:${seconds} '${milli}`
          , ss  : `${seconds} '${milli}`
          , s   : `${milli}`
    }
    return TimeResp
}

export const DateSingle = {
      wrap       : false
    , dateFormat : 'Y-m-d'
    , altFormat  : 'Y-m-d'
    , altInput   : true
    , defaultDate: moment().format("YYYY-MM-DD")
    , locale     : {rangeSeparator: " - "}
    , mode       : "single"
    , minDate    : "today"
}

export const DateRange = {
      wrap       : false
    , dateFormat : 'Y-m-d'
    , altFormat  : 'Y-m-d'
    , altInput   : true
    , defaultDate: moment().format("YYYY-MM-DD")
    , locale     : { rangeSeparator: " - " }
    , mode       : "range"
    , minDate    : "today"
 }